/**
 * templateGenerator.ts — the pane-side generator for the "use slide
 * templates" path.
 *
 * Runs after the user has approved a layout per slide in the wizard, and does
 * the three things only the pane can:
 *
 *   1. POST /v1/deckorate/slides/create-slide-template — one authorised call
 *      for the WHOLE deck, returning a MappingPlan per slide.
 *   2. Load the template library's signature catalog, which is what resolves
 *      a plan's symbolic slot ids ("body#0") to real shapes, and where each
 *      template's own .pptx lives (service/templateCatalogApi.ts).
 *   3. Patch + insert each slide in order (service/templateAssembler.ts) —
 *      `PowerPoint.run()` exists only here, never in the dialog window.
 *
 * WHY THE FIT CALL ISN'T PER-SLIDE
 * The endpoint takes the whole deck and isolates per-slide failures itself
 * (`status: "failed"` on that slide, the rest still fitted), so one request
 * is both fewer round trips and the contract's own unit of work. Progress is
 * still reported per slide: the deck's slides are inserted one at a time
 * after the fit returns, which is the part the user actually watches.
 *
 * Emits the same `GeneratorCallbacks` as the freehand generator, so the
 * progress UI is identical on both paths.
 */

import type { HttpClient } from "../../../services/deckorateApi";
import type {
  GenerationRun,
  GeneratorCallbacks,
  SlidePlan,
  TemplatePlan,
  TemplatePlanSlide,
  TemplateSlideGenerator,
} from "../hooks/types";
import { assembleAndInsert, buildSlotMap, prefetchTemplateSources, type PlanApplyReport } from "./templateAssembler";
import {
  blobPathFileUrl,
  entrySourceFileUrl,
  fetchTemplateCatalog,
  folderOfTemplateId,
  resolveCatalogEntry,
  type TemplateCatalog,
} from "./templateCatalogApi";
import { SlideApiError, type IntentTag } from "./slideGenerationApi";
import { fitSlidesToTemplates, templateBlobRef, type SlideFitResult } from "./templatesApi";

interface TemplateGeneratorDeps {
  /** Deckorate backend base URL (config.deckorateApiBaseUrl). */
  readonly baseUrl: string;
  /** Shared authorised client — used for the SAS mint the catalog needs. */
  readonly http: HttpClient;
  /**
   * Renders a slide WITHOUT its template, from the brief alone — used when
   * the template's own .pptx can't be reached (an unreadable library, an
   * unregistered folder). The content is still the AI's; only the reviewed
   * layout is lost, which beats putting nothing in the deck at all.
   *
   * Injected rather than imported so this module stays free of the host APIs
   * and the deck's theme, exactly as apiGenerator's `insertSlide` is.
   */
  readonly renderWithoutTemplate?: (slide: TemplatePlanSlide) => Promise<void>;
}

/** Map an approved plan slide to a wizard SlidePlan row (1-based index). */
function toSlidePlan(slide: TemplatePlanSlide, position: number): SlidePlan {
  return { index: position, title: slide.title, status: "waiting" };
}

/**
 * Resolves the .pptx a fit result should be patched into.
 *
 * An explicit blob location from the backend wins (see templatesApi.ts's
 * `templateBlobRef` — that's the agreed direction of travel); otherwise the
 * template is looked up in the catalog and its own source file resolved
 * through the library's directory SAS. Returns null when neither yields a
 * fetchable file, which the caller reports as that slide failing rather than
 * inserting an unpatched template.
 */
function resolveSourceUrl(catalog: TemplateCatalog, result: SlideFitResult): string | null {
  const blob = templateBlobRef(result);
  // A ready SAS URL is the only form that isn’t limited to the one library
  // the add-in mints a directory token for, so it wins outright.
  if (blob.sasUrl) return blob.sasUrl;
  // A path, by contrast, is rebuilt against THIS library’s folder — only its
  // final segment is used — so a path naming another folder resolves to a URL
  // that 404s rather than to the wrong file.
  // A path is signed by whichever library covers its folder, falling back to
  // the pick's own folder for a bare filename.
  const folder = folderOfTemplateId(result.template_id);
  if (blob.blobPath) return blobPathFileUrl(catalog, blob.blobPath, folder);
  if (blob.fileName) return blobPathFileUrl(catalog, blob.fileName, folder);

  const entry = resolveCatalogEntry(catalog, result.template_id);
  return entry ? entrySourceFileUrl(catalog, entry) : null;
}

/** The slide number inside the template file, from whichever source has it. */
function resolveTemplateSlideIndex(catalog: TemplateCatalog, result: SlideFitResult): number | null {
  const blob = templateBlobRef(result);
  if (blob.slideNumber) return blob.slideNumber;
  const entry = resolveCatalogEntry(catalog, result.template_id);
  if (entry?.slideIndex) return entry.slideIndex;
  // Last resort: the catalog id encodes it ("...Cover_Hero.pptx#2").
  const suffix = /#(\d+)\s*$/.exec(result.template_id)?.[1];
  return suffix ? Number.parseInt(suffix, 10) : null;
}

/**
 * Last resort for a slide whose template can't be used: render the brief with
 * the same structured model the freehand path's no-PPTX fallback uses.
 * Reports "done" with a detail saying the layout was skipped, so the deck
 * comes out complete and the user can still see which slides missed out.
 */
async function renderFallback(
  deps: TemplateGeneratorDeps,
  slide: TemplatePlanSlide | undefined,
  position: number,
  cb: GeneratorCallbacks,
  reason: string,
): Promise<boolean> {
  if (!deps.renderWithoutTemplate || !slide) return false;
  try {
    await deps.renderWithoutTemplate(slide);
    cb.onSlideUpdate(position, "done", "Added without its layout");
    console.warn(`[create-slide] slide ${position} rendered without its template: ${reason}`);
    return true;
  } catch (error) {
    console.error(`[create-slide] fallback render failed for slide ${position}:`, error);
    return false;
  }
}

/**
 * Falls back to the brief-only render when one's available; otherwise reports
 * the slide as failed. Collapses the three spots `insertFittedSlide` needs
 * this exact "try the fallback, else report" decision (no template resolved,
 * no slot map, and a hard insert failure) into one call each.
 */
async function fallbackOrReportError(
  deps: TemplateGeneratorDeps,
  planSlide: TemplatePlanSlide | undefined,
  position: number,
  cb: GeneratorCallbacks,
  reason: string,
  errorMessage: string,
): Promise<void> {
  if (await renderFallback(deps, planSlide, position, cb, reason)) return;
  cb.onSlideUpdate(position, "error", errorMessage);
}

/** Not a failure — the slide is in the deck, styled correctly, just with some
 *  slots left as the template had them. Worth a trace so terse output can be
 *  traced to a slot-id mismatch rather than a bad fit. */
function logUnresolvedSlots(position: number, unresolved: PlanApplyReport["unresolved"]): void {
  if (unresolved.length === 0) return;
  console.warn(`[create-slide] slide ${position}: ${unresolved.length} slot(s) not written`, unresolved);
}

/** Assembles + inserts one fitted slide, reflecting the outcome on the UI. */
async function insertFittedSlide(
  deps: TemplateGeneratorDeps,
  catalog: TemplateCatalog,
  result: SlideFitResult,
  position: number,
  planSlide: TemplatePlanSlide | undefined,
  cb: GeneratorCallbacks,
  sourceCache: Map<string, Promise<ArrayBuffer>>,
): Promise<void> {
  if (result.status === "failed") {
    cb.onSlideUpdate(position, "error", result.error ?? "Couldn't fit this slide's content");
    return;
  }

  const sourceFileUrl = resolveSourceUrl(catalog, result);
  const templateSlideIndex = resolveTemplateSlideIndex(catalog, result);
  if (!sourceFileUrl || !templateSlideIndex) {
    return fallbackOrReportError(
      deps,
      planSlide,
      position,
      cb,
      `layout "${result.template_id}" is in no readable template library`,
      `Layout "${result.template_id}" isn't available`,
    );
  }

  const entry = resolveCatalogEntry(catalog, result.template_id);
  // Slot ids come from the SIGNATURE placeholders, which is the same input
  // the backend built them from — see templateAssembler.ts's header.
  const slots = entry ? buildSlotMap(entry.placeholders, entry.slideDimensions) : new Map();
  const assignments = result.placeholder_assignments ?? {};
  if (slots.size === 0 && Object.keys(assignments).length > 0) {
    // The file is fetchable but its slot map isn't. Inserting it unpatched
    // would add a slide still reading "Click to add title", so render the
    // fitted content plainly instead.
    return fallbackOrReportError(
      deps,
      planSlide,
      position,
      cb,
      `no slot map for "${result.template_id}"`,
      `Couldn't map this slide's content onto "${result.template_id}"`,
    );
  }

  cb.onSlideUpdate(position, "generating", "Adding to deck");
  try {
    const report = await assembleAndInsert(
      { sourceFileUrl, templateSlideIndex, slots, assignments },
      sourceCache,
    );
    logUnresolvedSlots(position, report.unresolved);
    cb.onSlideUpdate(position, "done", "Added to deck");
  } catch (error) {
    console.error(`[create-slide] template insert failed for slide ${position}:`, error);
    // The template was unreachable or unusable — the content isn't lost.
    const message = error instanceof Error ? error.message : undefined;
    await fallbackOrReportError(deps, planSlide, position, cb, message ?? "insert failed", message ?? "Insert failed");
  }
}

export function createTemplateGenerator(deps: TemplateGeneratorDeps): TemplateSlideGenerator {
  return {
    generate(plan: TemplatePlan, cb: GeneratorCallbacks): GenerationRun {
      const controller = new AbortController();

      void (async () => {
        // Position in the deck, keyed by the backend's own slide_idx, so a
        // response that reorders slides still updates the right row.
        const positionByIdx = new Map<number, number>();
        plan.slides.forEach((slide, index) => positionByIdx.set(slide.slide_idx, index + 1));

        try {
          cb.onPhase("outlining");
          cb.onPlan(plan.slides.map((slide, index) => toSlidePlan(slide, index + 1)));
          cb.onProgress(0, plan.slides.length);

          // The catalog and the fit run together: neither needs the other,
          // and the fit is the slower of the two by far.
          const [catalog, fit] = await Promise.all([
            fetchTemplateCatalog(deps.http, { signal: controller.signal }),
            fitSlidesToTemplates(
              {
                request_id: plan.requestId,
                fit_mode: "generic",
                slides: plan.slides.map((slide) => ({
                  slide_idx: slide.slide_idx,
                  title: slide.title,
                  subtitle: slide.subtitle ?? null,
                  intent_tag: slide.intent_tag as IntentTag,
                  main_points: slide.main_points,
                  template_id: slide.template_id,
                })),
              },
              {
                baseUrl: deps.baseUrl,
                authorizedFetch: deps.http.fetch,
                signal: controller.signal,
              },
            ),
          ]);

          if (controller.signal.aborted) return cb.onPhase("cancelled");
          cb.onPhase("generating");

          // One download per source .pptx, not per slide — several slides
          // commonly come from the same template file.
          const sourceCache = new Map<string, Promise<ArrayBuffer>>();
          // ...and every file downloads at once, up front, rather than each
          // waiting for the previous slide's insert to finish.
          prefetchTemplateSources(
            fit.slides.flatMap((result) => {
              const url = result.status === "failed" ? null : resolveSourceUrl(catalog, result);
              return url ? [url] : [];
            }),
            sourceCache,
          );
          // Sequential by design (a promise chain, one slide after another):
          // concurrent insertSlidesFromBase64 calls race for the same
          // insertion point, so the deck order would be non-deterministic.
          // A cancel stops the chain before the next slide.
          const planByIdx = new Map(plan.slides.map((slide) => [slide.slide_idx, slide]));
          let completed = 0;
          await fit.slides.reduce(async (previous, result) => {
            await previous;
            if (controller.signal.aborted) return;
            const position = positionByIdx.get(result.slide_idx) ?? result.slide_idx + 1;
            await insertFittedSlide(
              deps,
              catalog,
              result,
              position,
              planByIdx.get(result.slide_idx),
              cb,
              sourceCache,
            );
            completed += 1;
            cb.onProgress(completed, plan.slides.length);
          }, Promise.resolve());

          if (controller.signal.aborted) return cb.onPhase("cancelled");
          cb.onPhase("done");
        } catch (error) {
          if (
            controller.signal.aborted ||
            (error instanceof DOMException && error.name === "AbortError")
          ) {
            return cb.onPhase("cancelled");
          }
          const message =
            error instanceof SlideApiError || error instanceof Error
              ? error.message
              : "Generation failed";
          console.error("[create-slide] template generation failed:", error);
          cb.onError(message);
          cb.onPhase("error");
        }
        return undefined;
      })();

      return { cancel: () => controller.abort() };
    },
  };
}
